import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Report, GenerateReportRequest } from '../../types';
import { apiService } from '../../services/api';
import MonthYearPicker from '../submissions/MonthYearPicker';
import ReportPreview from './ReportPreview';

const ReportGenerator: React.FC = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Advanced options
  const [modelPreference, setModelPreference] = useState<'claude-3.5-sonnet' | 'nova-pro'>('claude-3.5-sonnet');
  const [customInstructions, setCustomInstructions] = useState('');
  const [includeMetadata] = useState(true); // Always include metadata for now

  const loadExistingReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getReport(month, year);

      if (response?.error) {
        // Safe access to nested error properties
        const errorCode = response.error.error?.code;
        const errorMessage = response.error.error?.message;
        
        if (errorCode === 'REPORT_NOT_FOUND') {
          setReport(null);
        } else if (errorCode === 'ENDPOINT_NOT_FOUND' || 
                   errorMessage?.includes('404') ||
                   errorMessage?.includes('Not Found')) {
          // Endpoint not implemented yet
          setReport(null);
          setError('Report generation is not yet implemented in the backend.');
        } else {
          setError(errorMessage || 'Unable to load existing report.');
        }
      } else {
        // Handle potential double-nested data structure or direct object
        let reportData: Report | null = null;
        
        if (response?.data) {
          if (response.data && typeof response.data === 'object') {
            // Check if it's already a Report object or nested
            // API service returns { data: lambdaResponse }
            // Lambda returns { success: true, data: reportObject }
            const lambdaResponse = response.data as any;
            if (lambdaResponse.success && lambdaResponse.data) {
              reportData = lambdaResponse.data;
            } else {
              reportData = response.data; // Fallback for different response structure
            }
            
            // Ensure the report has required properties with safe assignment
            if (reportData && typeof reportData === 'object') {
              const safeReport = { ...reportData };
              if (!safeReport.id) safeReport.id = 'unknown';
              if (!safeReport.month) safeReport.month = month;
              if (!safeReport.year) safeReport.year = year;
              // Don't override existing content
              if (!safeReport.content) safeReport.content = 'No content generated';
              if (!safeReport.status) safeReport.status = 'draft';
              if (!safeReport.generatedAt) safeReport.generatedAt = new Date().toISOString();
              reportData = safeReport;
            }
          }
        }
        
        setReport(reportData);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setError('Unable to load report. Please try again.');
      setReport(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadExistingReport();
  }, [month, year]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const request: GenerateReportRequest = {
        month,
        year,
        options: {
          includeMetadata,
          modelPreference,
          customInstructions: customInstructions.trim() || undefined
        }
      };

      const response = await apiService.generateReport(request);

      if (response?.error) {
        // Safe access to nested error properties
        const errorCode = response.error.error?.code;
        const errorMessage = response.error.error?.message;
        
        if (errorCode === 'ENDPOINT_NOT_FOUND' || 
            errorMessage?.includes('404') ||
            errorMessage?.includes('Not Found')) {
          setError('Report generation is not yet implemented in the backend.');
        } else {
          // Fallback to top-level error message if nested structure doesn't exist
          setError(errorMessage || 'Unable to generate report. Please try again.');
        }
      } else {
        // Handle potential double-nested data structure or direct object
        let reportData: Report | null = null;
        
        if (response?.data) {
          if (response.data && typeof response.data === 'object') {
            // API service returns { data: lambdaResponse }
            // Lambda returns { success: true, data: reportObject }
            const lambdaResponse = response.data as any;
            if (lambdaResponse.success && lambdaResponse.data) {
              reportData = lambdaResponse.data;
            } else {
              reportData = response.data; // Fallback for different response structure
            }
            
            // Ensure the report has required properties with safe assignment
            if (reportData && typeof reportData === 'object') {
              const safeReport = { ...reportData };
              if (!safeReport.id) safeReport.id = 'unknown';
              if (!safeReport.month) safeReport.month = month;
              if (!safeReport.year) safeReport.year = year;
              // Don't override existing content
              if (!safeReport.content) safeReport.content = 'No content generated';
              if (!safeReport.status) safeReport.status = 'draft';
              if (!safeReport.generatedAt) safeReport.generatedAt = new Date().toISOString();
              reportData = safeReport;
            }
          }
        }
        
        setReport(reportData);
        setSuccess('Report generated successfully! The AI has analyzed team submissions and created a comprehensive monthly report.');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Unable to generate report. Please try again.');
    }

    setGenerating(false);
  };

  const handleApproveReport = async () => {
    if (!report) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use year-month format for report approval, not UUID
      const reportId = `${report.year}-${String(report.month).padStart(2, '0')}`;
      const response = await apiService.approveReport(reportId);

      if (response?.error) {
        // Safe access to nested error properties
        const errorCode = response.error.error?.code;
        const errorMessage = response.error.error?.message;
        
        if (errorCode === 'ENDPOINT_NOT_FOUND' || 
            errorMessage?.includes('404') ||
            errorMessage?.includes('Not Found')) {
          setError('Report approval is not yet implemented in the backend.');
        } else {
          setError(errorMessage || 'Unable to approve report. Please try again.');
        }
      } else {
        // Handle potential double-nested data structure or direct object
        let reportData: Report | null = null;
        
        if (response?.data) {
          if (response.data && typeof response.data === 'object') {
            // API service returns { data: lambdaResponse }
            // Lambda returns { success: true, data: reportObject }
            const lambdaResponse = response.data as any;
            if (lambdaResponse.success && lambdaResponse.data) {
              reportData = lambdaResponse.data;
            } else {
              reportData = response.data; // Fallback for different response structure
            }
            
            // Ensure the report has required properties with safe assignment
            if (reportData && typeof reportData === 'object') {
              const safeReport = { ...reportData };
              if (!safeReport.id) safeReport.id = report?.id || 'unknown';
              if (!safeReport.month) safeReport.month = report?.month || month;
              if (!safeReport.year) safeReport.year = report?.year || year;
              if (!safeReport.content) safeReport.content = report?.content || '';
              if (!safeReport.status) safeReport.status = 'approved';
              if (!safeReport.generatedAt) safeReport.generatedAt = report?.generatedAt || new Date().toISOString();
              reportData = safeReport;
            }
          }
        }
        
        setReport(reportData);
        setSuccess('Report approved and distributed successfully!');
      }
    } catch (error) {
      console.error('Error approving report:', error);
      setError('Unable to approve report. Please try again.');
    }

    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'approved':
        return 'success';
      case 'distributed':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        AI Report Generation
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Generate New Report
              </Typography>
              
              <MonthYearPicker
                month={month}
                year={year}
                onMonthChange={setMonth}
                onYearChange={setYear}
              />

              {/* Advanced Options */}
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Advanced Options</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>AI Model</InputLabel>
                        <Select
                          value={modelPreference}
                          label="AI Model"
                          onChange={(e) => setModelPreference(e.target.value as any)}
                        >
                          <MenuItem value="claude-3.5-sonnet">Claude 3.5 Sonnet (Default - Latest & Most Capable)</MenuItem>
                          <MenuItem value="nova-pro">Amazon Nova Pro (AWS Native)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Custom Instructions (Optional)"
                        multiline
                        rows={3}
                        value={customInstructions}
                        onChange={(e) => setCustomInstructions(e.target.value)}
                        placeholder="e.g., Focus on technical achievements, include specific metrics..."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}
            </CardContent>
            
            <CardActions>
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={generating}
                startIcon={generating ? <CircularProgress size={20} /> : null}
              >
                {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : report ? (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Report Status
                  </Typography>
                  <Chip 
                    label={report?.status?.toUpperCase() || 'UNKNOWN'} 
                    color={getStatusColor(report?.status || 'unknown') as any}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Generated: {report?.generatedAt ? formatDate(report.generatedAt) : 'Unknown'}
                </Typography>
                
                {report?.approvedAt && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Approved: {formatDate(report.approvedAt)}
                  </Typography>
                )}

                {report?.metadata && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Generation Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Submissions: {report.metadata.submissionCount || 0}
                    </Typography>
                    {report.metadata.modelUsed && (
                      <Typography variant="body2" color="text.secondary">
                        Model: {report.metadata.modelUsed}
                      </Typography>
                    )}
                    {report.metadata.processingTimeMs && (
                      <Typography variant="body2" color="text.secondary">
                        Processing Time: {report.metadata.processingTimeMs}ms
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
              
              <CardActions>
                {report?.status === 'draft' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApproveReport}
                    disabled={loading}
                  >
                    Approve & Distribute
                  </Button>
                )}
                
                <Button
                  variant="outlined"
                  onClick={handleGenerateReport}
                  disabled={generating || report?.status !== 'draft'}
                >
                  Regenerate
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => {
                    setReport(null);
                    loadExistingReport();
                  }}
                  disabled={loading}
                  sx={{ ml: 1 }}
                >
                  Refresh
                </Button>
              </CardActions>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary">
                  No report exists for {new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {report && (
          <Grid item xs={12}>
            <ReportPreview report={report} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ReportGenerator;
